import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { COLOR_SYSTEM } from "../../design/design-system";
import Icon from "../../atoms/Icon";
import Text from "../../atoms/Text";
import Button from "../../atoms/Button";
import { FeedbackItem, FEEDBACK_ITEMS, FEEDBACK_ITEM_KEYS, RATING_LABELS } from "../../../common/models/types";
import { useFeedback, Rating } from "../../../contexts/FeedbackContext";

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px;
  height: 100%;
  overflow-y: auto;
`;

const SimpleFeedbackStatus = styled.div`
  padding: 12px;
  background: ${COLOR_SYSTEM.get("Gray")?.[50]};
  border-radius: 8px;
  margin-bottom: 20px;
`;

const SimpleFeedbackHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const SimpleFeedbackContent = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding-left: 24px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  font-size: 13px;
`;

const StarDisplay = styled.div`
  display: flex;
  gap: 2px;
  margin-left: 4px;
`;

const pulseAnimation = keyframes`
  0%, 100% {
    background-color: transparent;
  }
  50% {
    background-color: ${COLOR_SYSTEM.get("Yellow")?.[100]};
  }
`;

const SectionTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};
  margin-bottom: 12px;
`;

const DetailCard = styled.div`
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[100]};
  border-radius: 12px;
  padding: 16px;
  background: ${COLOR_SYSTEM.get("Gray")?.[0]};
  animation: ${pulseAnimation} 0.8s ease-in-out 2;
  animation-delay: 0.15s;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const DetailSection = styled.div`
  &:not(:last-child) {
    padding-bottom: 16px;
    margin-bottom: 16px;
    border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[100]};
  }
`;

const SectionSubtitle = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  margin-bottom: 10px;
`;

const ItemTagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const ItemTag = styled.button<{ $isSelected: boolean; $variant: 'helpful' | 'unhelpful' }>`
  padding: 8px 12px;
  border-radius: 16px;
  border: 1px solid ${({ $isSelected, $variant }) =>
    $isSelected
      ? $variant === 'helpful'
        ? COLOR_SYSTEM.get("Blue")?.[400]
        : COLOR_SYSTEM.get("Orange")?.[400]
      : COLOR_SYSTEM.get("Gray")?.[300]};
  background: ${({ $isSelected, $variant }) =>
    $isSelected
      ? $variant === 'helpful'
        ? COLOR_SYSTEM.get("Blue")?.[50]
        : COLOR_SYSTEM.get("Orange")?.[50]
      : COLOR_SYSTEM.get("Gray")?.[0]};
  color: ${({ $isSelected, $variant }) =>
    $isSelected
      ? $variant === 'helpful'
        ? COLOR_SYSTEM.get("Blue")?.[600]
        : COLOR_SYSTEM.get("Orange")?.[600]
      : COLOR_SYSTEM.get("Gray")?.[600]};
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ $variant }) =>
      $variant === 'helpful'
        ? COLOR_SYSTEM.get("Blue")?.[400]
        : COLOR_SYSTEM.get("Orange")?.[400]};
    background: ${({ $variant }) =>
      $variant === 'helpful'
        ? COLOR_SYSTEM.get("Blue")?.[50]
        : COLOR_SYSTEM.get("Orange")?.[50]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TextAreaWrapper = styled.div`
  position: relative;
`;

const StyledTextArea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 12px;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[300]};
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${COLOR_SYSTEM.get("Blue")?.[400]};
  }

  &::placeholder {
    color: ${COLOR_SYSTEM.get("Gray")?.[400]};
  }
`;

const CharCounter = styled.div`
  text-align: right;
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
  margin-top: 4px;
`;

const SubmitButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
`;

const ThankYouContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  gap: 12px;
`;

const NotSubmittedContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  gap: 12px;
`;

const CountdownText = styled.div`
  margin-top: 16px;
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
`;

const StarRatingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
`;

const StarRatingContainer = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const RatingLabel = styled.span`
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  height: 20px;
  transition: opacity 0.15s ease;
`;

const StarButton = styled.button<{ $isActive: boolean; $isSubmitting: boolean }>`
  background: none;
  border: none;
  padding: 4px;
  cursor: ${({ $isSubmitting }) => ($isSubmitting ? 'wait' : 'pointer')};
  transition: transform 0.15s ease;

  &:hover {
    transform: scale(1.1);
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

interface AIReviewFeedbackFormProps {
  reviewId?: number;
}

const AIReviewFeedbackForm: React.FC<AIReviewFeedbackFormProps> = ({
  reviewId,
}) => {
  const { state, actions, phase } = useFeedback();
  const [likes, setLikes] = useState<FeedbackItem[]>([]);
  const [dislikes, setDislikes] = useState<FeedbackItem[]>([]);
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hoveredRating, setHoveredRating] = useState<number | null>(null);
  const [countdown, setCountdown] = useState(3);

  const maxLength = 500;

  // reviewId 변경 시 입력 상태 초기화
  useEffect(() => {
    setLikes([]);
    setDislikes([]);
    setComment("");
    setHoveredRating(null);
  }, [reviewId]);

  // phase가 thankYou로 변경되면 카운트다운 시작
  useEffect(() => {
    if (phase !== "thankYou") {
      setCountdown(3); // 리셋
      return;
    }

    if (countdown <= 0) return;

    const timer = setTimeout(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [phase, countdown]);

  // 별점 클릭 핸들러 (피드백 탭에서 직접 제출)
  const handleStarClick = async (rating: Rating) => {
    if (state.isSubmitting) return;
    await actions.submitRating(rating);
  };

  const handleLikeToggle = (item: FeedbackItem) => {
    // 이미 아쉬운 항목에 있으면 선택 불가
    if (dislikes.includes(item)) return;

    setLikes((prev) =>
      prev.includes(item)
        ? prev.filter((i) => i !== item)
        : [...prev, item]
    );
  };

  const handleDislikeToggle = (item: FeedbackItem) => {
    // 이미 유용한 항목에 있으면 선택 불가
    if (likes.includes(item)) return;

    setDislikes((prev) =>
      prev.includes(item)
        ? prev.filter((i) => i !== item)
        : [...prev, item]
    );
  };

  const handleSubmit = async () => {
    if (likes.length === 0 && dislikes.length === 0 && !comment.trim()) return;

    setIsSubmitting(true);
    try {
      await actions.setDetails({
        likes: likes.length > 0 ? likes : undefined,
        dislikes: dislikes.length > 0 ? dislikes : undefined,
        comment: comment.trim() || undefined,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = likes.length > 0 || dislikes.length > 0 || comment.trim().length > 0;

  // phase === 'rating': 별점 입력 대기
  if (phase === "rating") {
    return (
      <FormContainer>
        <NotSubmittedContainer>
          <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[700]}>
            이 검토안에 만족하시나요?
          </Text>
          <StarRatingWrapper>
            <StarRatingContainer>
              {[1, 2, 3, 4, 5].map((starValue) => {
                const isActive = hoveredRating !== null ? starValue <= hoveredRating : false;
                return (
                  <StarButton
                    key={starValue}
                    $isActive={isActive}
                    $isSubmitting={state.isSubmitting}
                    onClick={() => handleStarClick(starValue as Rating)}
                    onMouseEnter={() => setHoveredRating(starValue)}
                    onMouseLeave={() => setHoveredRating(null)}
                    disabled={state.isSubmitting}
                    aria-label={`${starValue}점`}
                  >
                    <Icon
                      name="Star"
                      size={28}
                      color={isActive ? COLOR_SYSTEM.get("Yellow")?.[500] : COLOR_SYSTEM.get("Gray")?.[300]}
                    />
                  </StarButton>
                );
              })}
            </StarRatingContainer>
            <RatingLabel>
              {hoveredRating ? RATING_LABELS[hoveredRating] : "\u00A0"}
            </RatingLabel>
          </StarRatingWrapper>
        </NotSubmittedContainer>
      </FormContainer>
    );
  }

  // phase === 'thankYou': 의견 추가 완료 상태
  if (phase === "thankYou") {
    return (
      <FormContainer>
        <ThankYouContainer>
          <Icon name="Check Circle" size={48} color={COLOR_SYSTEM.get("Blue")?.[500]} />
          <Text textType="Headline" color={COLOR_SYSTEM.get("Gray")?.[800]}>
            피드백을 제출해주셔서 감사합니다!
          </Text>
          <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[600]}>
            더 나은 서비스를 제공하는 데 소중한 의견으로 활용하겠습니다.
          </Text>
          <CountdownText>
            이 화면은 {countdown}초 후에 사라집니다.
          </CountdownText>
        </ThankYouContainer>
      </FormContainer>
    );
  }

  // phase === 'hidden': 컴포넌트 숨김
  if (phase === "hidden") {
    return null;
  }

  // phase === 'adminView': Admin 읽기 전용 모드
  if (phase === "adminView") {
    return (
      <FormContainer>
        <SimpleFeedbackStatus>
          <SimpleFeedbackHeader>
            <Icon name="Check" size={16} color={COLOR_SYSTEM.get("Blue")?.[600]} />
            <Text textType="Body2" color={COLOR_SYSTEM.get("Blue")?.[700]}>
              피드백이 제출되었습니다
            </Text>
          </SimpleFeedbackHeader>
          <SimpleFeedbackContent>
            만족도:
            <StarDisplay>
              {[1, 2, 3, 4, 5].map((starValue) => (
                <Icon
                  key={starValue}
                  name="Star"
                  size={14}
                  color={starValue <= (state.rating || 0) ? COLOR_SYSTEM.get("Yellow")?.[500] : COLOR_SYSTEM.get("Gray")?.[300]}
                />
              ))}
            </StarDisplay>
            ({state.rating || 0}/5)
          </SimpleFeedbackContent>
        </SimpleFeedbackStatus>

        {/* 상세 피드백이 있는 경우 표시 */}
        {(state.likes?.length || state.dislikes?.length || state.comment) && (
          <DetailCard style={{ animation: 'none' }}>
            {/* 유용했던 항목 */}
            {state.likes && state.likes.length > 0 && (
              <DetailSection>
                <SectionSubtitle>유용했던 항목</SectionSubtitle>
                <ItemTagsContainer>
                  {state.likes.map((item) => (
                    <ItemTag
                      key={`like-${item}`}
                      $isSelected={true}
                      $variant="helpful"
                      disabled
                      style={{ cursor: 'default', opacity: 1 }}
                    >
                      {FEEDBACK_ITEMS[item]}
                      <Icon
                        name="Thumbs Up Filled"
                        size={14}
                        color={COLOR_SYSTEM.get("Blue")?.[500]}
                        style={{ marginLeft: 4 }}
                      />
                    </ItemTag>
                  ))}
                </ItemTagsContainer>
              </DetailSection>
            )}

            {/* 아쉬운 항목 */}
            {state.dislikes && state.dislikes.length > 0 && (
              <DetailSection>
                <SectionSubtitle>아쉬운 항목</SectionSubtitle>
                <ItemTagsContainer>
                  {state.dislikes.map((item) => (
                    <ItemTag
                      key={`dislike-${item}`}
                      $isSelected={true}
                      $variant="unhelpful"
                      disabled
                      style={{ cursor: 'default', opacity: 1 }}
                    >
                      {FEEDBACK_ITEMS[item]}
                      <Icon
                        name="Thumbs Down Filled"
                        size={14}
                        color={COLOR_SYSTEM.get("Orange")?.[500]}
                        style={{ marginLeft: 4 }}
                      />
                    </ItemTag>
                  ))}
                </ItemTagsContainer>
              </DetailSection>
            )}

            {/* 추가 의견 */}
            {state.comment && (
              <DetailSection>
                <SectionSubtitle>추가 의견</SectionSubtitle>
                <StyledTextArea
                  value={state.comment}
                  readOnly
                  style={{ backgroundColor: COLOR_SYSTEM.get("Gray")?.[50], cursor: 'default' }}
                />
              </DetailSection>
            )}
          </DetailCard>
        )}
      </FormContainer>
    );
  }

  // phase === 'detail': 제출 완료 + 의견 추가 가능 상태
  return (
    <FormContainer>
      {/* 간단 피드백 제출 상태 */}
      <SimpleFeedbackStatus>
        <SimpleFeedbackHeader>
          <Icon name="Check" size={16} color={COLOR_SYSTEM.get("Blue")?.[600]} />
          <Text textType="Body2" color={COLOR_SYSTEM.get("Blue")?.[700]}>
            간단 피드백이 제출되었습니다
          </Text>
        </SimpleFeedbackHeader>
        <SimpleFeedbackContent>
          만족도:
          <StarDisplay>
            {[1, 2, 3, 4, 5].map((starValue) => (
              <Icon
                key={starValue}
                name="Star"
                size={14}
                color={starValue <= (state.rating || 0) ? COLOR_SYSTEM.get("Yellow")?.[500] : COLOR_SYSTEM.get("Gray")?.[300]}
              />
            ))}
          </StarDisplay>
          ({state.rating || 0}/5)
        </SimpleFeedbackContent>
      </SimpleFeedbackStatus>

      {/* 의견 추가 섹션 */}
      <SectionTitle>
        좀 더 자세한 의견을 남겨주실 수 있나요?
      </SectionTitle>

      <DetailCard>
        {/* 유용했던 항목 */}
        <DetailSection>
          <SectionSubtitle>유용했던 항목</SectionSubtitle>
          <ItemTagsContainer>
            {FEEDBACK_ITEM_KEYS.map((item) => {
              const isDisabled = dislikes.includes(item);
              const isSelected = likes.includes(item);
              return (
                <ItemTag
                  key={`like-${item}`}
                  $isSelected={isSelected}
                  $variant="helpful"
                  onClick={() => handleLikeToggle(item)}
                  disabled={isDisabled}
                  title={isDisabled ? "아쉬운 항목에서 이미 선택됨" : undefined}
                >
                  {FEEDBACK_ITEMS[item]}
                  {isSelected && (
                    <Icon
                      name="Thumbs Up Filled"
                      size={14}
                      color={COLOR_SYSTEM.get("Blue")?.[500]}
                      style={{ marginLeft: 4 }}
                    />
                  )}
                </ItemTag>
              );
            })}
          </ItemTagsContainer>
        </DetailSection>

        {/* 아쉬운 항목 */}
        <DetailSection>
          <SectionSubtitle>아쉬운 항목</SectionSubtitle>
          <ItemTagsContainer>
            {FEEDBACK_ITEM_KEYS.map((item) => {
              const isDisabled = likes.includes(item);
              const isSelected = dislikes.includes(item);
              return (
                <ItemTag
                  key={`dislike-${item}`}
                  $isSelected={isSelected}
                  $variant="unhelpful"
                  onClick={() => handleDislikeToggle(item)}
                  disabled={isDisabled}
                  title={isDisabled ? "유용한 항목에서 이미 선택됨" : undefined}
                >
                  {FEEDBACK_ITEMS[item]}
                  {isSelected && (
                    <Icon
                      name="Thumbs Down Filled"
                      size={14}
                      color={COLOR_SYSTEM.get("Orange")?.[500]}
                      style={{ marginLeft: 4 }}
                    />
                  )}
                </ItemTag>
              );
            })}
          </ItemTagsContainer>
        </DetailSection>

        {/* 추가 의견 */}
        <DetailSection>
          <SectionSubtitle>추가 의견</SectionSubtitle>
          <TextAreaWrapper>
            <StyledTextArea
              value={comment}
              onChange={(e) => setComment(e.target.value.slice(0, maxLength))}
              placeholder={`• 약사님께 더 도움이 되려면 어떤 점을 개선하면 좋을까요?\n• 더 필요한 기능이 있으신가요?\n• 특히 좋았던 점을 알려주셔도 좋아요.`}
              maxLength={maxLength}
            />
          </TextAreaWrapper>
          <CharCounter>
            {comment.length} / {maxLength}
          </CharCounter>
        </DetailSection>
      </DetailCard>

      {/* 제출 버튼 */}
      <SubmitButtonWrapper>
        <Button
          type="Text"
          size="Medium"
          color="Primary"
          variant="Contained"
          onClick={handleSubmit}
          disabled={!canSubmit || isSubmitting}
          disabledColor={!canSubmit || isSubmitting}
        >
          {isSubmitting ? "제출 중..." : "의견 추가 제출"}
        </Button>
      </SubmitButtonWrapper>
    </FormContainer>
  );
};

export default AIReviewFeedbackForm;
